import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { transformPromotedSpecialtiesForTopNavExp } from '../../../Homepage/transformPromotedSpecialtiesForTopNavExp';
import { useBreakpoints } from '../../../bookings/util/useBreakpoints';
import { useExperiment } from '../../ExperimentContext/useExperiment';
import { DesktopTopNavExperiment } from '../../Experiments/DesktopTopNavExperiment';
import { useHomepageTiles } from '../../Experiments/HomepageTilesExperiment';
import { TELEHEALTH_SERVICES_LANDING_URL } from '../../constants';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useRouter } from '../../hooks/useRouter';
import { useSearch } from '../../hooks/useSearch';
import { useFetch } from '../../services/NetworkService';
import { env } from '../../util/env';
import { SearchPromotionItem } from '../SearchHeader/Suggestions/EmptySearchSuggestions/SearchPromotionResponse.type';
import { OverflowMenu } from './SpecialtyOption/OverflowMenu';
import { OverflowSpecialtyOption } from './SpecialtyOption/OverflowSpecialtyOption';
import {
  GPIcon,
  GPSpecialtiesContainer,
  Specialty,
  Logo,
  LogoLink,
  SpecialtiesContainer,
  TelehealthIcon,
  TopNavContainer,
  OverflowSpecialtiesContainer,
  TopNavContainerVariant2,
  SpecialtyVariant2,
  WhiteTelehealthIcon,
  WhiteGPIcon,
  OverflowSpecialtyOptionVariant2,
  WhiteLogo,
} from './TopNavigationBar.styles';

export type TopNavSpecialtyOption = {
  text: string;
  iconElement: typeof TelehealthIcon | typeof GPIcon;
  original: SearchPromotionItem;
  url?: string;
} & SearchPromotionItem;

export type PromotedSpecialtiesFetchResult = {
  data: SearchPromotionItem[];
};

const isGpSpecialty = (specialty: SearchPromotionItem) =>
  specialty.type === 'telehealth' || specialty.title === 'General Practice';

// Only call this with the GP specialties
const getGPSpecialtyContent = (
  specialty: SearchPromotionItem,
  isVariant2 = false,
): TopNavSpecialtyOption => {
  if (specialty.type === 'telehealth')
    return {
      text: 'GP Telehealth',
      iconElement: isVariant2 ? WhiteTelehealthIcon : TelehealthIcon,
      original: specialty,
      url: TELEHEALTH_SERVICES_LANDING_URL + '/?source=dtn',
      ...specialty,
    };
  return {
    text: 'GP In-person',
    iconElement: isVariant2 ? WhiteGPIcon : GPIcon,
    original: specialty,
    ...specialty,
  };
};

export const TopNavigationBar = () => {
  const [fetchedSpecialties, setFetchedSpecialties] = useState<PromotedSpecialtiesFetchResult>({
    data: [],
  });

  const [showMore, setShowMore] = useState(false);
  const [overflowText, setOverflowText] = useState('See more');
  const { toggleInlineSearch, toggleDesktopSearch } = useSearch();
  const [otherSpecialties, setOtherSpecialties] = useState<SearchPromotionItem[]>([]);
  const [overflowSpecialties, setOverflowSpecialties] = useState<SearchPromotionItem[]>([]);
  const router = useRouter();
  const breakpoints = useBreakpoints();
  const fetch = useFetch();

  // The homepage tiles experiment moves the search bar to the search header. We
  // need to use the inline search's search bar when the homepage tiles experiment
  // isn't running
  const inline = !useHomepageTiles() && router?.pathname === '/';

  const onClickOverflow = useCallback(() => {
    setShowMore(!showMore);
    setOverflowText(overflowText === 'See more' ? 'See less' : 'See more');
  }, [overflowText, showMore]);

  const onClickSearch = useCallback(
    (text: string) => {
      inline ? toggleInlineSearch(text) : toggleDesktopSearch(text);
      showMore && onClickOverflow();
    },
    [inline, onClickOverflow, showMore, toggleDesktopSearch, toggleInlineSearch],
  );

  const overflowOptionRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(overflowOptionRef, () => {
    setShowMore(false);
    setOverflowText('See more');
  });

  useEffect(() => {
    void fetch(`${env.MEGATRON_HOST}/api/4/search/promoted`)
      .then(response => response.json())
      .then(setFetchedSpecialties);
  }, [fetch]);

  const specialties: SearchPromotionItem[] = useMemo(
    () => transformPromotedSpecialtiesForTopNavExp(fetchedSpecialties.data),
    [fetchedSpecialties.data],
  );

  const topNavExperiment = useExperiment(DesktopTopNavExperiment);
  const isVariant2 = topNavExperiment.data?.variant === 2;

  // Could use .reduce(), but this is more readable
  const gpSpecialties = specialties
    .filter(specialty => isGpSpecialty(specialty))
    .map(specialty => getGPSpecialtyContent(specialty, isVariant2));
  //.reverse();

  // Dynamically change the number of specialties shown on the navbar based on screen width
  const getNumberOfShownSpecialties = useCallback(() => {
    if (breakpoints.Medium) return 0;
    if (breakpoints.Large) return 1;
    if (breakpoints.XLarge) return 4;

    // Growth document requires 7 specialties to show, but we can't do that in a nice way
    // without changing useBreakpoints(). Since this is an experiment, we do 6
    return 6;
  }, [breakpoints.Large, breakpoints.Medium, breakpoints.XLarge]);

  useEffect(() => {
    const limit = getNumberOfShownSpecialties();

    setOtherSpecialties(specialties.filter(specialty => !isGpSpecialty(specialty)).slice(0, limit));

    setOverflowSpecialties(specialties.filter(specialty => !isGpSpecialty(specialty)).slice(limit));
  }, [
    breakpoints.Large,
    breakpoints.Medium,
    breakpoints.XLarge,
    breakpoints.XXLarge,
    getNumberOfShownSpecialties,
    specialties,
  ]);

  const Container = isVariant2 ? TopNavContainerVariant2 : TopNavContainer;
  const SpecialtyLink = isVariant2 ? SpecialtyVariant2 : Specialty;
  const OverflowSpecialty = isVariant2 ? OverflowSpecialtyOptionVariant2 : OverflowSpecialtyOption;
  const LogoComponent = isVariant2 ? WhiteLogo : Logo;

  return (
    <Container>
      <LogoLink to="/">
        <LogoComponent />
      </LogoLink>
      <GPSpecialtiesContainer>
        {gpSpecialties.map((specialty, idx) => (
          <SpecialtyLink
            key={idx}
            url={specialty.url}
            text={specialty.text}
            Icon={specialty.iconElement}
            onClick={() => onClickSearch(specialty.title)}
            analyticsId={`Desktop Top Nav ${specialty.original.title}`}
          />
        ))}
      </GPSpecialtiesContainer>
      <SpecialtiesContainer>
        {otherSpecialties.map((specialty, idx) => (
          <SpecialtyLink
            key={idx}
            text={specialty.topNavTitle ?? specialty.title}
            onClick={() => onClickSearch(specialty.title)}
            analyticsId={`Desktop Top Nav ${specialty.title}`}
          />
        ))}

        <OverflowSpecialtiesContainer ref={overflowOptionRef}>
          <OverflowSpecialty
            text={overflowText}
            overflowSpecialties={overflowSpecialties}
            onClick={onClickOverflow}
            menuIsOpen={showMore}
          />
          {showMore && (
            <OverflowMenu overflowSpecialties={overflowSpecialties} onClick={onClickSearch} />
          )}
        </OverflowSpecialtiesContainer>
      </SpecialtiesContainer>
    </Container>
  );
};
